import {BaseComponent} from '../baseComponent';
import * as React from 'react';
import Modal from 'react-modal';
import {tracked} from '../../decorators/tracked';
import {TicketType} from '../../models/ticketType';
import {Server} from '../../services/server';
import {lazyInject} from '../../services/container';
import {HttpError} from '../../errors/httpError';
import {BAD_REQUEST, CONFLICT, FORBIDDEN, UNAUTHORIZED} from 'http-status-codes';
import {TicketSignup} from '../../models/ticketSignup';
import {Facebook} from '../../services/facebook';
import {bind} from '../../decorators/bind';
import {History} from 'history';
import {Translate} from '../../services/translate';
import classNames from 'classnames';
import tickIcon from '../../assets/images/tick_icon.svg';
import fbIcon from '../../assets/images/fb_icon_blue.jpg';
import fbWhiteIcon from '../../assets/images/facebook_icon_small_white.png';
import {getCurrentUser, updateUserEmail, User} from "../../models/user";

enum ModalState
{
    // Loading indicator
    Loading,
    // Need to log in to facebook notice
    Notice,
    // Facebook login was canceled
    Canceled,
    // Not all needed permissions were granted
    NotGranted,
    // User has no email on facebook
    NoEmail,
    // There was an error
    Error,
    // No tickets left
    NoTickets,
    // Already signed up for this ticket
    AlreadySignedUp,
    // User has email on facebook, but we need them to confirm it
    ConfirmEmail
}

interface ReserveTicketModalProps
{
    ticketType: TicketType|null;
    history: History;
    onDismiss: () => void;
    showModal?: boolean;
}

export class ReserveTicketModal extends BaseComponent<ReserveTicketModalProps>
{
    @tracked
    private modalState: ModalState = ModalState.Loading;
    
    @tracked
    private emailInputValue: string = '';

    @tracked
    private termsInputValue: any = false;

    private emailInputRef: HTMLInputElement|null;
    
    @lazyInject(Server)
    private server: Server;
    
    @lazyInject(Facebook)
    private facebook: Facebook;
    
    @lazyInject(Translate)
    private translate: Translate;
    
    private get isEmailInputValid(): boolean
    {
        // The second one is used to make sure we only accept a@b.c and not a@b
        return !!this.emailInputValue
            && /\S+@\S+\.\S+/.test(this.emailInputValue)
            /*&& this.emailInputValue.indexOf('freemail.hu') < 0*/;
    }

    public componentWillReceiveProps(nextProps: Readonly<ReserveTicketModalProps>, nextContext: any): void
    {
        if (this.props.ticketType === nextProps.ticketType)
            return;
        
        this.modalState = ModalState.Loading;
    }

    public render(): React.ReactNode
    {
        return <Modal
            isOpen={!!this.props.ticketType || !!this.props.showModal}
            className={{
                base: `modal-content`,
                afterOpen: 'modal-content-after-open',
                beforeClose: 'modal-content-before-close'
            }}
            overlayClassName={{
                base: `modal-overlay modal-overlay-${ModalState[this.modalState].toLowerCase()}`,
                afterOpen: 'modal-overlay-after-open',
                beforeClose: 'modal-overlay-before-close'
            }}
            onAfterOpen={this.onAfterModalOpen}
        >
            {this.renderContent()}
        </Modal>;
    }
    
    private renderContent(): React.ReactNode
    {
        switch (this.modalState)
        {
            case ModalState.Loading:
                return <div className="sk-circle">
                    <div className="sk-circle1 sk-child" />
                    <div className="sk-circle2 sk-child" />
                    <div className="sk-circle3 sk-child" />
                    <div className="sk-circle4 sk-child" />
                    <div className="sk-circle5 sk-child" />
                    <div className="sk-circle6 sk-child" />
                    <div className="sk-circle7 sk-child" />
                    <div className="sk-circle8 sk-child" />
                    <div className="sk-circle9 sk-child" />
                    <div className="sk-circle10 sk-child" />
                    <div className="sk-circle11 sk-child" />
                    <div className="sk-circle12 sk-child" />
                </div>;
            case ModalState.Notice:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('reserveNotice')}
                    </div>
                    <div>
                        <a className="facebook-oauth-button" href={`/api/facebook-login?returnPath=${location.pathname}/${this.props.ticketType?.id}`}>
                            <img src={fbWhiteIcon} alt=""/>
                            <span>
                                {this.translate.translate("continueWithFacebook")}
                            </span>
                        </a>
                        {/*<FacebookLoginButton onLogin={this.onFacebokButtonLogin} />*/}
                    </div>
                </>;
            case ModalState.Canceled:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('canceledFacebookLogin')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.props.onDismiss}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.NotGranted:
                return null;
            case ModalState.ConfirmEmail:
            case ModalState.NoEmail:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className={classNames({'reserve-text': true, 'confirm-email': this.modalState === ModalState.ConfirmEmail})}>
                        <p>
                            {this.translate.translate(
                                this.modalState === ModalState.NoEmail ? 'enterEmail' : 'confirmEmail')
                            }
                        </p>
                        <p style={{fontSize: '.6em'}}>
                            {this.translate.translate('freemailNote')}
                        </p>
                    </div>
                    <div className="reserve-input-wrapper">
                        <input
                            type="email"
                            ref={ref => this.emailInputRef = ref}
                            value={this.emailInputValue}
                            onChange={event => this.emailInputValue = event.target.value}
                            required
                        />
                    </div>
                    <div className="terms">
                        <input
                            type="checkbox"
                            checked={this.termsInputValue}
                            onChange={event => this.onTermsChanged(event)}
                        />
                        <span onClick={() => {this.termsInputValue = !this.termsInputValue}}>{this.translate.translate('acceptTerms')}</span>
                    </div>
                    <div className="submit-button-wrapper">
                        <button
                            type="button"
                            className="reserve-ok-button"
                            onClick={this.onEnteredEmail}
                            disabled={!this.isEmailInputValid || !this.termsInputValue }
                        >
                            <span className="facebook-ok-button-label">{this.translate.translate('reserve')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.Error:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('bookingError')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.props.onDismiss}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.NoTickets:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('noTicketsLeft')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.onRefreshPage}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;
            case ModalState.AlreadySignedUp:
                return <>
                    <div className="reserve-icon-wrapper">
                        <img src={fbIcon} alt=""/>
                    </div>
                    <div className="reserve-text">
                        {this.translate.translate('alreadyBookedClubcard')}
                    </div>
                    <div>
                        <button type="button" className="reserve-ok-button" onClick={this.onRefreshPage}>
                            <span className="facebook-ok-button-label">{this.translate.translate('okay')}</span>
                            <img src={tickIcon} alt=""/>
                        </button>
                    </div>
                </>;                
            default:
                return null;
        }
    }
    
    @bind
    public async onAfterModalOpen(): Promise<void>
    {
        await this.facebookLogin(false);

        // await this.trySignup();
        // await this.facebookLogin(false);
    }
    
    @bind
    private async onLoginClick(): Promise<void>
    {
        try
        {
            await this.facebookLogin(false);
        }
        catch (err)
        {
            this.modalState = ModalState.Error;
            throw err;
        }
    }

    private onTermsChanged(event: any): void
    {
        this.termsInputValue = (event.target as HTMLInputElement).checked;
    }

    // @bind
    // private async onFacebokButtonLogin(event: facebook.StatusResponse)
    // {
    //     if (!event.authResponse)
    //     {
    //         this.modalState = ModalState.Canceled;
    //         return;
    //     }
    //
    //     this.facebook.lastAuthResponse = event.authResponse;
    //
    //     const meResponse = await this.facebook.me(['email']);
    //
    //     this.emailInputValue = meResponse.email || '';
    //     this.modalState = meResponse.email ? ModalState.ConfirmEmail : ModalState.NoEmail;
    // }
    
    private async facebookLogin(reauthorize: boolean): Promise<void>
    {
        this.modalState = ModalState.Loading;

        const user = await getCurrentUser(true);
        
        if (!user)
        {
            this.modalState = ModalState.Notice;
            return;
            // await this.facebook.login(reauthorize);
        }

        // console.debug('facebook.login response', this.facebook.lastAuthResponse);

        // if (!this.facebook.lastAuthResponse)
        // {
        //     this.modalState = ModalState.Canceled;
        //     return;
        // }

        // const grantedScopes = (response.authResponse.grantedScopes || '').split(',');
        //
        // if (grantedScopes.indexOf('email') < 0)
        // {
        //     // this.modalState = ModalState.NotGranted;
        //     this.emailInputValue = '';
        //     this.modalState = ModalState.NoEmail;
        //     return;
        // }

        // const meResponse = await this.facebook.me(['email']);
        
        // if (!meResponse.email)
        // {
            this.emailInputValue = user.email || '';
            this.modalState = user.email ? ModalState.ConfirmEmail : ModalState.NoEmail;
        //     return;
        // }

        // await this.tryLogin();
        // await this.trySignup();
    }
    
    private async trySignup(): Promise<void>
    {
        try
        {
            await this.server.post(`ticket-signups`, TicketSignup.fromTicketType(this.props.ticketType as TicketType));
        }
        catch (err)
        {
            if (err instanceof HttpError)
            {
                // Try to show a detailed error
                switch (err.response.status)
                {
                    case FORBIDDEN:
                    case UNAUTHORIZED:
                        // // Wait for api to load, so we can synchronously call Fb.login from the button click event. Otherwise it
                        // // won't work
                        // await this.facebook.promise;
                        this.modalState = ModalState.Notice;
                        return;
                    case BAD_REQUEST:
                    {
                        const responseBody = await err.response.json();

                        if (responseBody && responseBody.message === 'No tickets left')
                        {
                            this.modalState = ModalState.NoTickets;
                            return;
                        }
                        break;
                    }
                    case CONFLICT:
                    {
                        const responseBody = await err.response.json();

                        if (responseBody && responseBody.message === 'Already signed up')
                        {
                            this.modalState = ModalState.AlreadySignedUp;
                            return;
                        }
                        break;
                    }
                }
            }

            // Fallback to generic error
            this.modalState = ModalState.Error;
            throw err;
        }

        this.props.history.push(this.translate.formatLink('/sikeres-foglalas'));
    }
    
    // private async tryLogin(response?: facebook.StatusResponse, email?: string): Promise<void>
    // {
    //     this.modalState = ModalState.Loading;
    //
    //     if (!response)
    //     {
    //         response = await this.facebook.getLoginStatus();
    //     }
    //
    //     await this.server.post(`login`, {
    //         provider: 'facebook',
    //         token: response.authResponse.accessToken,
    //         email
    //     });
    // }
    
    @bind
    private async onEnteredEmail()
    {
        if (!this.isEmailInputValid)
            return;
        
        try
        {
            await updateUserEmail(this.emailInputValue);
            // await this.tryLogin(undefined, this.emailInputValue);
            await this.trySignup();
        }
        catch (err)
        {
            this.modalState = ModalState.Error;
            throw err;
        }
    }
    
    @bind
    private onRefreshPage()
    {
        this.modalState = ModalState.Loading;

        // If we're on a ticket link, clear that
        const match = location.pathname.match(/esemenyek\/\d+(\/\d+)/);
        if (match) {
            location.href = location.pathname.slice(0, location.pathname.length - match[1].length) ;
        } else {
            location.reload();
        }
    }
}